import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useMangoApi } from '~/atoms/MangoApi'
import { transformCallLogExtensionCallerIds } from '~/queries/callQueries'
import { CallTranscript } from '~/types/CallTranscript'
import { getCallLogFromCall } from '~/utils/callLogUtils'

import {
  ApiError,
  CallLog,
  ExtensionPreview,
  PaginatedApiResult,
} from '@mangovoice/types'

export const useGetTranscribedCalls = (
  pbxId: number,
  options: Omit<UseQueryOptions<CallLog[], ApiError>, 'queryKey'> = {},
) => {
  const api = useMangoApi()
  return useQuery<CallLog[], ApiError>({
    queryKey: ['transcribedCalls', pbxId],
    queryFn: async () => {
      if (pbxId === 0) return Promise.resolve([])

      const { ok: extensionResultOk, data: extensionData } =
        await api.getPbxExtensions(pbxId)
      if (!extensionResultOk) return Promise.reject(extensionData as ApiError)

      const { ok, data } = await api.getTranscribedCallList(pbxId)
      if (!ok) {
        return Promise.reject(data)
      }

      const extensionDataResults = (
        extensionData as PaginatedApiResult<ExtensionPreview>
      ).results

      const transformedCallLogs = transformCallLogExtensionCallerIds(
        (data?.results ?? []).map((call) => getCallLogFromCall(call)),
        extensionDataResults,
      )
      return transformedCallLogs
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useGetCallTranscript = (
  pbxId: number,
  callId: number,
  onQuerySuccess: ((data: CallTranscript) => void) | undefined = undefined,
  onQueryFailed: ((error: ApiError) => void) | undefined = undefined,
  options: Record<string, unknown> = {},
) => {
  const api = useMangoApi()
  return useQuery({
    queryKey: ['callTranscript', pbxId, callId],
    queryFn: async () => {
      const { ok: findTranscriptOk, data: findTranscriptData } =
        await api.findCallTranscriptByCallId(pbxId, callId)

      if (!findTranscriptOk) {
        const error = {
          message: 'could not find transcript',
          status: 404,
        }
        onQueryFailed?.(error)
        return Promise.reject(error)
      }

      const transcriptId = findTranscriptData!.results[0].id
      //fetch the transcript by id
      const { ok, data } = await api.getCallTranscript(transcriptId)
      if (!ok) {
        onQueryFailed?.(data as ApiError)
        return Promise.reject(data)
      }

      onQuerySuccess?.(data!.data as CallTranscript)
      return data!.data as CallTranscript
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}
